import React, { FC, useRef, useState } from "react";
import { inject, observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import Box from "react-raster";
import { Avatar, Button, Icon } from "src/components";
import { Space, User } from "src/models";
import { getRoutePath, Page } from "src/routes";
import { AuthStore, RoutingStore, RootStore, SpaceStore} from "src/stores";
import { SpaceType } from "src/types";
import { useClickOutside } from "src/util/util";
import styled from "styled-components";
import {theme } from "../config/theme";

export interface IUserMenuProps {
  authStore?:    AuthStore;
  rootStore?:    RootStore;
  routingStore?: RoutingStore;
  spaceStore?:   SpaceStore;
}

const MenuContainer = styled.div`
  z-index:          10;
  background-color: ${theme.color.white};
  box-shadow:       ${theme.boxShadow.medium};
  color:            ${theme.color.black};
  cursor:           pointer;
  height:           fit-content;
  position:         absolute;
  border-radius:    0.4rem;
  right:            0.5rem;
  top:              5.5rem;
  width:            16rem;
`;

const MenuItemWrapper = styled.div`
  border-bottom: 1px solid ${theme.color.primaryBorder};
  border-top:    1px solid ${theme.color.primaryBorder};
  width:         15rem;
`

const MenuHeader = styled.div`
  align-items:     center;
  display:         flex;
  flex-direction:  row;
  font-weight:     ${theme.font.weight.bold};
  font-size:       ${theme.font.size.small};
  justify-content: space-between;
  padding-bottom:  1rem;
  padding:         0.75rem 1rem;
`;

const WorkspaceList = styled.ul<{hide: boolean}>`
  font-size:   ${theme.font.size.small};
  width:       15rem;
  margin-left: 0.5rem;
`;

const WorkspaceListEntry = styled.li<{hide: boolean}>`
  align-items:    center;
  display:        flex;
  font-weight:    ${theme.font.weight.bold};
  height:         ${(props) => (props.hide ? "0" : "3.2rem")};
  opacity:        ${(props) => (props.hide ? "0" : "1")};
  overflow:       hidden;
  padding-left:   0.5rem;
  text-overflow:  ellipsis;
  white-space:    nowrap;
  transition:     height 0.3s ease-in-out, opacity 0.3s ease-in-out;

  :hover {
    background: ${theme.color.hover};
  }
`;

const MenuItem = styled.li`
  cursor:          pointer;
  font-size:       ${theme.font.size.small};
  font-weight:     ${theme.font.weight.normal};
  list-style-type: none;
  padding:         0.75rem 0.75rem;

  :hover {
    background: ${theme.color.hover};
  }
`;

const MenuFooter = styled.div`
  align-items:     center;
  display:         flex;
  flex-direction:  row;
  font-weight:     ${theme.font.weight.bold};
  font-size:       ${theme.font.size.small};
  justify-content: space-between;
  padding:         0.5rem 1rem;
`;

export const UserMenu: FC<IUserMenuProps> = inject("rootStore", "authStore", "spaceStore", "routingStore")(observer((props: IUserMenuProps): JSX.Element => {
  const { t, i18n } = useTranslation();

  const [isOpen,    setIsOpen]   = useState<boolean>(false);
  const [hideList,  setHideList] = useState<boolean>(true);
  const {rootStore, authStore, spaceStore, routingStore } = props;
  const toggleMenu       = React.useCallback(() => setIsOpen(!isOpen), [isOpen]);
  const user: User       = authStore.getCurUser;
  const workspace: Space = authStore.getCurWorkspaceID
    ? spaceStore.getItemByID(authStore.getCurWorkspaceID)
    : null;
  const workspaces: Space[] = (workspace && spaceStore.getItemsByType(SpaceType.Workspace).filter(s => s.id !== workspace.id)) || [];
  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, () => setIsOpen(false));

  const logout = async () => {
    await authStore.logout();
    routingStore.push("/login");
  };

  if (!authStore.isLoggedIn) return null;

  return (
    <Box position="relative" display="flex" onClick={() => toggleMenu()}>
      <Button variant="icon">
        <Avatar size="medium" image={user.getAvatar} initials={user.default_initials} />
      </Button>
      {isOpen && (
        <MenuContainer ref={wrapperRef}>
          <MenuHeader onClick={(e) => {setHideList(!hideList); e.stopPropagation();}}>
            {workspace?.title || ""}
            <Button 
              variant  = "icon"
              disabled = {!authStore.getCurUser?.canEditSpace(authStore.getCurWorkspaceID)}
              onClick  = {() =>
                routingStore.push(
                  getRoutePath(Page.WorkSpaceSettings, {
                    id: authStore.getCurWorkspaceID
                }))}
            >
              <Icon iconId="settings" size="medium" color={theme.color.black}/>
            </Button>
          </MenuHeader>
          <WorkspaceList hide={hideList}>
          {workspaces.map(s => (
            <WorkspaceListEntry 
              key     = {s.id}
              hide    = {hideList}
              onClick = {(e) => {
                spaceStore.changeWorkspace(s.id);
                setHideList(true);
                e.stopPropagation();
              }}
            >
              {s.title}
            </WorkspaceListEntry>
            ))}
          </WorkspaceList>
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <MenuItemWrapper>
              <MenuItem onClick={() => rootStore.toggleFeedbackModal()}>
                {t("feedback")}
              </MenuItem>
              {/* <MenuItem onClick={() => routingStore.push("/manual")}>
                {t("user_manual")}
              </MenuItem> */}
              <MenuItem onClick={() => routingStore.push("/user/settings")}>
                {t("settings")}
              </MenuItem>
              {/* <MenuItem onClick={() => routingStore.push("/components")}>
                {t("views_library")}
              </MenuItem> */}
            </MenuItemWrapper>
          </Box>
          <MenuFooter onClick={logout}>
            {t("logout")}
            <Button variant="icon">
            <Icon iconId="logOut" size="medium" color={theme.color.black}/>
            </Button>
          </MenuFooter>
        </MenuContainer>
      )}
    </Box>
  );
}));
